import { useEffect, useMemo, useRef, useState } from 'react';
import { renderDocx, renderPptx, renderXlsx, rendererFor } from './rawRenderers';
import { ARCDISK_API_URL } from '../../config';
import { ExternalLink, Inbox, X } from '../../lib/icons';
import { Badge, Button, Modal, Skeleton } from '../ds';
import AssetThumb from '../features/AssetThumb';
import { useStudentSubmissions } from '../../data/hooks';
import { useAssetUrl } from '../../data/hooks';
import { cn } from '../../lib/cn';

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Media-custody chips: only problem states earn a chip on the section header —
// a processed file's normality is the default, not an announcement.
const PROBLEM_TONE = { failed: 'danger', unsupported: 'warning', external: 'info' };

// Tika stores XHTML (pre-existing, recorded follow-up: Accept: text/plain on
// the extraction nodes). Strip markup for reading; collapse the whitespace
// the tags leave behind, but keep paragraph breaks.
function stripMarkup(text) {
  if (!text) return '';
  return String(text)
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// A quiet gap where the reader couldn't read — a pause, not an alarm. The
// honest per-section total lives in the header chip; stripping gaps entirely
// would let a partial transcript masquerade as complete.
function GapMark() {
  return (
    <span
      title="couldn’t be read"
      aria-label="unreadable text"
      className="mx-1 select-none rounded bg-amber-100 px-1 text-[10px] font-bold tracking-[0.2em] text-amber-700/90"
    >
      ···
    </span>
  );
}

// The student's ink, one line per line — worksheet answers and numbered facts
// read as the stanza they are, not smushed prose.
function InkText({ text }) {
  const lines = String(text)
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    <>
      {lines.map((line, li) => {
        const parts = line.split(/\[illegible\]/g);
        return (
          <span key={li} className="block">
            {parts.map((part, i) => (
              <span key={i}>
                {part}
                {i < parts.length - 1 && <GapMark />}
              </span>
            ))}
          </span>
        );
      })}
    </>
  );
}

// OCR v2 anchors each region to its panel and puts the panel's own heading on
// the region's FIRST LINE — surface that structure instead of a text wall.
function splitRegion(text) {
  const lines = String(text)
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length > 1 && lines[0].length <= 48 && !/[.?!]$/.test(lines[0])) {
    return { heading: lines[0], body: lines.slice(1).join('\n') };
  }
  return { heading: null, body: lines.join('\n') };
}

function gapsIn(text) {
  return (String(text).match(/\[illegible\]/g) || []).length;
}

// A region whose text is ONLY illegible markers carries no reading value —
// it folds into the section's gap count instead of rendering as a paragraph.
function isNoise(region) {
  return String(region.text ?? '').replace(/\[illegible\]/g, '').trim() === '';
}

function regionsOf(asset) {
  const r = asset.understanding?.regions;
  return Array.isArray(r) ? r.filter((x) => x && String(x.text ?? '').trim() !== '') : [];
}

// Find `needle` in `hay` ignoring whitespace/case differences — evidence
// quotes are verbatim from the COMPOSED text, whose spacing differs from this
// viewer's stripped rendering. Returns [start, end] in hay's ORIGINAL indices
// (so the match can be wrapped in <mark> without altering the text), or null.
// Zero-width chars (ZWSP/ZWNJ/ZWJ/BOM/soft hyphen) appear INSIDE words in some
// PDF text layers; JS \s doesn't match them, so they must be skipped outright —
// dropping one never inserts a space, or intra-word occurrences would split words.
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF\u00AD]/;

function findLoose(hay, needle) {
  if (!hay || !needle) return null;
  const norm = [];
  const map = [];
  let prevSpace = true;
  for (let i = 0; i < hay.length; i++) {
    if (ZERO_WIDTH.test(hay[i])) continue;
    if (/\s/.test(hay[i])) {
      if (!prevSpace) {
        norm.push(' ');
        map.push(i);
        prevSpace = true;
      }
    } else {
      norm.push(hay[i].toLowerCase());
      map.push(i);
      prevSpace = false;
    }
  }
  const n = String(needle)
    .replace(new RegExp(ZERO_WIDTH.source, 'g'), '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!n) return null;
  const idx = norm.join('').indexOf(n);
  if (idx === -1) return null;
  return [map[idx], map[idx + n.length - 1] + 1];
}

// A document's text with the evidence passage marked and scrolled into view —
// the landing spot of "Show in work": the teacher arrives ON the quoted words.
function HighlightedDoc({ text, highlight, className }) {
  const markRef = useRef(null);
  const range = useMemo(() => findLoose(text, highlight), [text, highlight]);
  useEffect(() => {
    if (range) {
      const t = setTimeout(
        () => markRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        250
      );
      return () => clearTimeout(t);
    }
    return undefined;
  }, [range]);
  if (!range) return <p className={className}>{text}</p>;
  return (
    <p className={className}>
      {text.slice(0, range[0])}
      <mark ref={markRef} className="rounded bg-primary/15 px-0.5 text-foreground ring-1 ring-primary/30">
        {text.slice(range[0], range[1])}
      </mark>
      {text.slice(range[1])}
    </p>
  );
}

// One photo's reading section: the photo sticky beside its transcript at
// width; hovering a handwritten paragraph outlines its region ON the photo
// (bbox is normalized [x,y,w,h] from OCR v2 — older artifacts simply have
// none and the hover degrades to text emphasis). Printed scaffold collapses:
// context on demand, never confused with the student's work.
function PhotoSection({ asset, sectionRef, highlight }) {
  const { data, isError } = useAssetUrl(asset.id);
  const [hoverIdx, setHoverIdx] = useState(null);

  const regions = regionsOf(asset);
  // Evidence anchoring: the region containing the quoted passage stays
  // emphasized (same treatment as hover) so the teacher lands on the words
  // the grader cited.
  const pinIdx = useMemo(() => {
    if (!highlight) return null;
    const hit = regions.findIndex((r) => findLoose(String(r.text || ''), highlight));
    return hit === -1 ? null : hit;
  }, [regions, highlight]);
  const allHandwritten = regions
    .map((r, idx) => ({ ...r, idx }))
    .filter((r) => r.provenance !== 'printed');
  const handwritten = allHandwritten.filter((r) => !isNoise(r));
  const printed = regions.filter((r) => r.provenance === 'printed' && !isNoise(r));
  // The honest summary: inline gaps + regions too far gone to render at all.
  const gapCount =
    allHandwritten.reduce((n, r) => n + (isNoise(r) ? 1 : gapsIn(r.text)), 0);
  const bornVisual = asset.understanding?.classification === 'born_visual';
  const caption = asset.understanding?.caption || '';
  const outlineIdx = hoverIdx ?? pinIdx;
  const hoverBox =
    outlineIdx != null && Array.isArray(regions[outlineIdx]?.bbox) && regions[outlineIdx].bbox.length === 4
      ? regions[outlineIdx].bbox
      : null;

  return (
    <section ref={sectionRef} data-asset-id={asset.id} className="rounded-xl border border-border bg-card p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <span className="truncate text-[13px] font-semibold text-foreground">
          {asset.original_name || `Photo ${asset.id}`}
        </span>
        <Badge tone="info">{bornVisual ? 'born-visual' : 'photo'}</Badge>
        {asset.origin !== 'file' && <Badge tone="outline">{asset.origin.replace('_', ' ')}</Badge>}
        {gapCount > 0 && (
          <Badge tone="warning">
            {gapCount} unreadable {gapCount === 1 ? 'patch' : 'patches'}
          </Badge>
        )}
        {PROBLEM_TONE[asset.status] && <Badge tone={PROBLEM_TONE[asset.status]}>{asset.status}</Badge>}
      </header>

      <div className={cn('gap-4', bornVisual ? 'flex flex-col' : 'md:grid md:grid-cols-[minmax(0,320px)_1fr]')}>
        <div className={cn('relative self-start', !bornVisual && 'md:sticky md:top-2')}>
          {data?.url ? (
            <a href={data.url} target="_blank" rel="noreferrer" title="Open full size" className="relative block">
              {/* Presigned bytes bypass Vercel by design — plain img on purpose. */}
              <img
                src={data.url}
                alt={asset.original_name || `Photo ${asset.id}`}
                className={cn('w-full rounded-lg border border-border object-contain', bornVisual ? 'max-h-[420px]' : 'max-h-[360px]')}
              />
              {hoverBox && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute rounded border-2 border-primary shadow-[0_0_0_2px_rgba(255,255,255,0.6)]"
                  style={{
                    left: `${hoverBox[0] * 100}%`,
                    top: `${hoverBox[1] * 100}%`,
                    width: `${hoverBox[2] * 100}%`,
                    height: `${hoverBox[3] * 100}%`,
                  }}
                />
              )}
            </a>
          ) : isError ? (
            // Demo: the file bytes come from the ArcDisk backend; if it is not
            // reachable say so instead of leaving a shimmer forever.
            <div className="flex aspect-[4/3] w-full items-center justify-center rounded-lg border border-dashed border-border bg-secondary/40 px-3 text-center text-[12px] text-muted-foreground">
              Preview unavailable — the original is in the student’s Drive folder.
            </div>
          ) : (
            <Skeleton className="aspect-[4/3] w-full rounded-lg" />
          )}
          {bornVisual && caption && (
            <p className="mt-1.5 text-[12px] italic leading-snug text-muted-foreground">{caption}</p>
          )}
        </div>

        <div className="mt-3 min-w-0 md:mt-0">
          {handwritten.length > 0 ? (
            <div className="flex max-w-[62ch] flex-col gap-3">
              {handwritten.map((r) => {
                const { heading, body } = splitRegion(r.text);
                return (
                  <div
                    key={r.idx}
                    onMouseEnter={() => setHoverIdx(r.idx)}
                    onMouseLeave={() => setHoverIdx(null)}
                    title={r.page_area || undefined}
                    className={cn(
                      'border-l-[3px] py-0.5 pl-3 transition-colors',
                      hoverIdx === r.idx || pinIdx === r.idx
                        ? 'border-primary bg-primary/5'
                        : 'border-primary/35'
                    )}
                  >
                    {heading && (
                      <div className="mb-0.5 text-[12px] font-bold uppercase tracking-wide text-foreground/80">
                        <InkText text={heading} />
                      </div>
                    )}
                    <div className="text-[13.5px] leading-relaxed text-foreground">
                      <InkText text={body} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : bornVisual ? null : (
            <p className="text-[12.5px] italic text-muted-foreground">
              No student writing was read from this photo{caption ? ` — ${caption}` : '.'}
            </p>
          )}

          {!bornVisual && caption && handwritten.length > 0 && (
            <p className="mt-2 text-[11.5px] italic text-muted-foreground/80">{caption}</p>
          )}

          {printed.length > 0 && (
            <details className="mt-3 rounded-lg border border-dashed border-border bg-secondary/30 px-3 py-2">
              <summary className="cursor-pointer select-none text-[11.5px] font-semibold text-muted-foreground">
                Worksheet print · {printed.length} block{printed.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-2 flex flex-col gap-2">
                {printed.map((r, i) => (
                  <p key={i} className="whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground">
                    {r.text}
                  </p>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </section>
  );
}

// A document's reading section. Two faithful views, per section: ORIGINAL —
// the file's own Drive preview (real formatting, embedded images; each ASSET
// carries its drive_file_id, so multi-doc containers preview every file) —
// and TEXT, the extraction the grader actually read. Original is the default
// when Drive knows the file; uploads without provenance fall back to text.
function DocSection({ asset, fallbackText, decorativeCount = 0, sectionRef, highlight }) {
  const text = stripMarkup(asset.understanding?.tika_text ?? fallbackText ?? '');
  const canPreview = Boolean(asset.drive_file_id);
  // Evidence anchoring lands in TEXT mode — the Drive preview can't mark a
  // passage; the extraction (what the grader actually read) can. The mode is
  // DERIVED, not snapshotted: the highlight arrives only after the assets
  // load and the anchor resolves, i.e. AFTER mount — a useState initializer
  // would have already locked the section into Original and the mark would
  // never render. The user's explicit toggle still wins once touched.
  const [userMode, setUserMode] = useState(null);
  // DEMO: default to the extracted TEXT even when a Drive preview exists. The
  // Drive iframe asks the viewer to sign in to a Google account with access to
  // the school's folder — on a client's screen that reads as a broken demo.
  // "Original" stays one click away for presenters who are signed in.
  const mode = userMode ?? 'text';
  const setMode = setUserMode;

  return (
    <section ref={sectionRef} data-asset-id={asset.id} className="rounded-xl border border-border bg-card p-4">
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <span className="truncate text-[13px] font-semibold text-foreground">
          {asset.original_name || `Document ${asset.id}`}
        </span>
        <Badge tone="neutral">document</Badge>
        {PROBLEM_TONE[asset.status] && <Badge tone={PROBLEM_TONE[asset.status]}>{asset.status}</Badge>}
        {canPreview && (
          <span className="ml-auto flex overflow-hidden rounded-lg border border-border">
            {[
              ['original', 'Original'],
              ['text', 'Text'],
            ].map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'px-2 py-0.5 text-[11px] font-medium transition-colors',
                  mode === m
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                )}
              >
                {label}
              </button>
            ))}
          </span>
        )}
      </header>
      {mode === 'original' && canPreview ? (
        <iframe
          src={`https://drive.google.com/file/d/${encodeURIComponent(asset.drive_file_id)}/preview`}
          title={asset.original_name || 'Document'}
          className="h-[480px] w-full rounded-lg border border-border"
          allow="autoplay"
        />
      ) : text ? (
        <HighlightedDoc
          text={text}
          highlight={highlight}
          className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-foreground"
        />
      ) : (
        <p className="text-[12.5px] italic text-muted-foreground">
          No text has been extracted from this document yet.
        </p>
      )}
      {decorativeCount > 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground/80">
          {decorativeCount} decorative graphic{decorativeCount === 1 ? '' : 's'} from this document
          hidden (clip art / backgrounds — not student work).
        </p>
      )}
    </section>
  );
}

// Teacher/admin viewer for a student's work on a project — the Work view
// (media-ux design §2, U·1): a sources rail of real thumbnails and ONE
// reading pane of per-file sections in page order. Opened from StudentList.
// `anchor` (optional): { assetId, highlight } — evidence deep link. Opens
// scrolled to the owning section; text quotes arrive MARKED in the extraction,
// photo quotes arrive with their region emphasized (media-ux: evidence is a
// receipt, and a receipt you can walk back to the work is one a teacher can
// trust). When assetId is null the owning section is found by searching the
// quote across the container's documents and photo regions.
// ── DEMO: raw mode ──────────────────────────────────────────────────────────
// Before a project is assessed the viewer must show ONLY what the student
// handed in — no OCR transcript, no regions, captions or classification, no
// extracted sub-assets — because all of that is the pipeline's output and
// would give the assessment away. These two sections render the original
// bytes and nothing else. The host passes `raw` (StudentWorkPanel sets it to
// !assessed); the graded sections above are untouched.
function RawPhotoSection({ asset, sectionRef }) {
  const { data, isError } = useAssetUrl(asset.id);
  return (
    <section ref={sectionRef} data-asset-id={asset.id} className="rounded-xl border border-border bg-card p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <span className="truncate text-[13px] font-semibold text-foreground">
          {asset.original_name || `Photo ${asset.id}`}
        </span>
        <Badge tone="info">photo</Badge>
        {data?.url && (
          <a href={data.url} target="_blank" rel="noreferrer" className="ml-auto text-[11px] font-semibold text-primary hover:underline">
            Open full size
          </a>
        )}
      </header>
      {data?.url ? (
        <img
          src={data.url}
          alt={asset.original_name || `Photo ${asset.id}`}
          className="mx-auto max-h-[560px] w-auto max-w-full rounded-lg border border-border object-contain"
        />
      ) : isError ? (
        <div className="flex aspect-[4/3] w-full items-center justify-center rounded-lg border border-dashed border-border bg-secondary/40 px-3 text-center text-[12px] text-muted-foreground">
          Preview unavailable — the original is in the student’s Drive folder.
        </div>
      ) : (
        <Skeleton className="aspect-[4/3] w-full rounded-lg" />
      )}
    </section>
  );
}

// The original document, rendered in the browser from its bytes (docx-preview
// paginates the .docx with its real fonts, images and layout). Bytes come
// from the ArcDisk backend's /api/media/:id/bytes — a presigned bucket URL
// can't be fetch()ed (no CORS on the bucket) and the hosted Google/Office
// viewers render presigned links blank. PDFs the browser renders itself.
function RawDocSection({ asset, sectionRef }) {
  const { data } = useAssetUrl(asset.id);
  const containerRef = useRef(null);
  const [state, setState] = useState('loading');
  const kind = rendererFor(asset);
  const isPdf = kind === 'pdf';

  useEffect(() => {
    if (isPdf) return undefined;
    let alive = true;
    const url = ARCDISK_API_URL ? `${ARCDISK_API_URL}/api/media/${encodeURIComponent(asset.id)}/bytes` : null;
    if (!url || !kind) {
      setState('error');
      return undefined;
    }
    setState('loading');
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return kind === 'docx' ? r.blob() : r.arrayBuffer();
      })
      .then((bytes) => {
        if (!alive || !containerRef.current) return undefined;
        const el = containerRef.current;
        if (kind === 'docx') return renderDocx(bytes, el);
        if (kind === 'xlsx') return renderXlsx(bytes, el);
        return renderPptx(bytes, el);
      })
      .then(() => alive && setState('ready'))
      .catch(() => alive && setState('error'));
    return () => {
      alive = false;
    };
  }, [asset.id, kind, isPdf]);

  return (
    <section ref={sectionRef} data-asset-id={asset.id} className="rounded-xl border border-border bg-card p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <span className="truncate text-[13px] font-semibold text-foreground">
          {asset.original_name || `Document ${asset.id}`}
        </span>
        <Badge tone="neutral">document</Badge>
        <span className="ml-auto flex items-center gap-3">
          {data?.url && (
            <a href={data.url} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-primary hover:underline">
              Download original
            </a>
          )}
          {asset.drive_file_id && (
            <a
              href={`https://drive.google.com/file/d/${encodeURIComponent(asset.drive_file_id)}/view`}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] font-semibold text-muted-foreground hover:underline"
            >
              Open in Drive
            </a>
          )}
        </span>
      </header>
      {isPdf ? (
        data?.url ? (
          <iframe src={data.url} title={asset.original_name || 'Document'} className="h-[640px] w-full rounded-lg border border-border bg-white" />
        ) : (
          <Skeleton className="h-[640px] w-full rounded-lg" />
        )
      ) : (
        <>
          {state === 'loading' && <Skeleton className="h-[640px] w-full rounded-lg" />}
          {state === 'error' && (
            <p className="text-[12.5px] italic text-muted-foreground">
              Preview unavailable — use “Download original” to open the file.
            </p>
          )}
          {/* No inner scroll box: the pages stack in the viewer's one reading
              pane, so a wheel over the document scrolls the document and its
              page count is obvious at a glance. */}
          <div
            ref={containerRef}
            className={cn('arcdisk-docx overflow-x-auto rounded-lg border border-border', state !== 'ready' && 'hidden')}
          />
        </>
      )}
    </section>
  );
}

export default function SubmissionViewer({ projectId, student, onClose, anchor = null, raw = false }) {
  const open = Boolean(student);
  const studentId = student?.student_id;
  const { data: subs, isLoading, error } = useStudentSubmissions(projectId, studentId, {
    enabled: open,
  });

  // Post-container there is normally ONE submission per student; legacy
  // multi-row students pick between containers here.
  const [activeSubId, setActiveSubId] = useState(null);
  const active = useMemo(
    () => subs?.find((s) => s.id === activeSubId) ?? subs?.[0] ?? null,
    [subs, activeSubId]
  );

  // Reading order: parents in page order, each parent's extracted children
  // directly after it (the payload arrives page-ordered; nesting is local).
  // Decorative extractions (student_work=false from D3 — clip art, icons,
  // template backgrounds inside a document) carry no reading value: they stay
  // out of the rail and the pane, surfaced only as a count on the parent.
  const isDecorative = (a) =>
    a.kind === 'image' &&
    a.parent_asset_id != null &&
    a.understanding?.student_work === false;
  const decorativeCountOf = (assets, parentId) =>
    assets.filter((a) => a.parent_asset_id === parentId && isDecorative(a)).length;
  const orderedAssets = useMemo(() => {
    const assets = active?.assets ?? [];
    const parents = assets.filter((a) => a.parent_asset_id == null);
    // DEMO raw mode: the files as submitted, no extracted children.
    if (raw) return parents;
    const childrenOf = (id) =>
      assets.filter((a) => a.parent_asset_id === id && !isDecorative(a));
    return parents.flatMap((p) => [p, ...childrenOf(p.id)]);
  }, [active, raw]);

  // Scrollspy: the section in view lights its rail thumb.
  const scrollRef = useRef(null);
  const sectionRefs = useRef(new Map());
  const [inViewId, setInViewId] = useState(null);
  useEffect(() => {
    if (!open || orderedAssets.length === 0) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (hit) setInViewId(Number(hit.target.dataset.assetId));
      },
      { root: scrollRef.current, threshold: [0.2, 0.5] }
    );
    sectionRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [open, orderedAssets]);

  const jumpTo = (assetId) => {
    sectionRefs.current.get(assetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Resolve the evidence anchor to its owning section once the assets arrive:
  // an explicit assetId wins; otherwise the quote is searched across document
  // extractions and photo regions. The matched section receives the highlight.
  const anchorTarget = useMemo(() => {
    if (!open || !anchor || orderedAssets.length === 0) return null;
    if (anchor.assetId != null) return anchor.assetId;
    if (!anchor.highlight) return null;
    for (const a of orderedAssets) {
      if (a.kind === 'document') {
        const t = stripMarkup(a.understanding?.tika_text ?? active?.text_content ?? '');
        if (findLoose(t, anchor.highlight)) return a.id;
      } else if (regionsOf(a).some((r) => findLoose(String(r.text || ''), anchor.highlight))) {
        return a.id;
      }
    }
    // No verbatim match (grades from before the verbatim-evidence era quote
    // paraphrased corpora): land on the first document — the likeliest owner
    // — rather than silently not scrolling at all.
    const firstDoc = orderedAssets.find((a) => a.kind === 'document');
    return (firstDoc ?? orderedAssets[0]).id;
  }, [open, anchor, orderedAssets, active]);

  useEffect(() => {
    if (anchorTarget == null) return undefined;
    const t = setTimeout(
      () =>
        sectionRefs.current
          .get(anchorTarget)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      200
    );
    return () => clearTimeout(t);
  }, [anchorTarget]);

  const setSectionRef = (assetId) => (el) => {
    if (el) sectionRefs.current.set(assetId, el);
    else sectionRefs.current.delete(assetId);
  };

  return (
    <Modal open={open} onClose={onClose} labelledBy="sv-title" className="h-[88vh] max-w-6xl">
      <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-3.5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <h2 id="sv-title" className="truncate text-base font-bold text-foreground">
              {student?.student_name || 'Student'}
            </h2>
            <p className="text-xs text-muted-foreground">
              {active
                ? `${orderedAssets.length} file${orderedAssets.length === 1 ? '' : 's'} · ${fmtDate(active.created_at)}`
                : 'Work'}
            </p>
          </div>
          {subs && subs.length > 1 && (
            <select
              value={active?.id ?? ''}
              onChange={(e) => setActiveSubId(Number(e.target.value))}
              className="rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground"
              aria-label="Choose submission"
            >
              {subs.map((s) => (
                <option key={s.id} value={s.id}>
                  {fmtDate(s.created_at)} · {s.file_name || `Submission ${s.id}`}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          {active?.drive_file_id && (
            <a
              href={`https://drive.google.com/file/d/${encodeURIComponent(active.drive_file_id)}/view`}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="outline" size="sm">
                <ExternalLink /> Drive
              </Button>
            </a>
          )}
          <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
            <X />
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <aside className="w-52 shrink-0 overflow-y-auto border-r border-border bg-secondary/30 p-2.5">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-destructive">
              <Inbox className="size-5 opacity-70" />
              Couldn’t load the work.
              <span className="text-xs text-muted-foreground">{error.message}</span>
            </div>
          ) : orderedAssets.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-6 text-center text-xs text-muted-foreground">
              <Inbox className="size-5 opacity-60" />
              No files registered.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {orderedAssets.map((a) => (
                <button
                  key={a.id}
                  onClick={() => jumpTo(a.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg border bg-card p-1.5 text-left transition-colors',
                    a.parent_asset_id != null && 'ml-3',
                    inViewId === a.id
                      ? 'border-primary shadow-[0_0_0_2px_rgba(91,83,230,0.15)]'
                      : 'border-border hover:border-primary/40'
                  )}
                >
                  <AssetThumb asset={a} className="h-9 w-12 shrink-0" />
                  <span className="min-w-0">
                    <span className="block truncate text-[11px] font-semibold leading-tight text-foreground">
                      {a.original_name || `${a.kind} ${a.id}`}
                    </span>
                    <span className="block text-[10px] text-muted-foreground">
                      {/* raw mode never shows pipeline status ("unsupported") */}
                      {!raw && PROBLEM_TONE[a.status] ? a.status : a.kind}
                      {a.origin === 'embedded' && ' · extracted'}
                      {a.origin === 'page_render' && ' · page'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto bg-background">
          {!active && !isLoading ? (
            <div className="flex h-full items-center justify-center p-10 text-sm text-muted-foreground">
              No work found for this student.
            </div>
          ) : (
            <div className="flex flex-col gap-3 p-4">
              {orderedAssets.map((a) =>
                raw ? (
                  a.kind === 'image' ? (
                    <RawPhotoSection key={a.id} asset={a} sectionRef={setSectionRef(a.id)} />
                  ) : (
                    <RawDocSection key={a.id} asset={a} sectionRef={setSectionRef(a.id)} />
                  )
                ) : a.kind === 'image' ? (
                  <PhotoSection
                    key={a.id}
                    asset={a}
                    sectionRef={setSectionRef(a.id)}
                    highlight={anchorTarget === a.id ? (anchor?.highlight ?? null) : null}
                  />
                ) : a.status === 'unsupported' ? (
                  // The pipeline read nothing from this file (spreadsheets), so
                  // there is no transcript to show — render the original.
                  <RawDocSection key={a.id} asset={a} sectionRef={setSectionRef(a.id)} />
                ) : (
                  <DocSection
                    key={a.id}
                    asset={a}
                    fallbackText={orderedAssets.length === 1 ? active?.text_content : null}
                    decorativeCount={decorativeCountOf(active?.assets ?? [], a.id)}
                    sectionRef={setSectionRef(a.id)}
                    highlight={anchorTarget === a.id ? (anchor?.highlight ?? null) : null}
                  />
                )
              )}
              {!raw && orderedAssets.length === 0 && active?.text_content && (
                <section className="rounded-xl border border-border bg-card p-4">
                  <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-foreground">
                    {stripMarkup(active.text_content)}
                  </p>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}